import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('clientcheck_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('clientcheck_token');
            localStorage.removeItem('clientcheck_user');
            window.location.href = '/login';
        }
        // Handle 403 "Use attestation required" - trigger attestation modal
        if (error.response?.status === 403 && 
            error.response?.data?.detail === 'Use attestation required') {
            // Dispatch custom event that AuthContext can listen to
            window.dispatchEvent(new CustomEvent('attestation-required'));
        }
        // Handle 429 "Daily lookup limit reached" - trigger lookup limit modal
        if (error.response?.status === 429 && 
            error.response?.data?.detail?.error === 'Daily lookup limit reached') {
            const hoursRemaining = error.response?.data?.detail?.resets_in_hours || 24;
            window.dispatchEvent(new CustomEvent('lookup-limit-reached', { 
                detail: { hoursRemaining } 
            }));
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    signup: (data) => api.post('/auth/signup', data),
    login: (data) => api.post('/auth/login', data),
    getMe: () => api.get('/auth/me'),
    acceptAttestation: () => api.post('/auth/accept-attestation'),
    // Email verification
    verifyEmail: (token) => api.post('/auth/verify-email', new URLSearchParams({ token }), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    }),
    resendVerification: (email) => api.post('/auth/resend-verification', new URLSearchParams({ email }), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    }),
    // Password reset
    requestPasswordReset: (email) => api.post('/auth/request-password-reset', new URLSearchParams({ email }), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    }),
    resetPassword: (token, newPassword) => api.post('/auth/reset-password', 
        new URLSearchParams({ token, new_password: newPassword }), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    })
};

// Address API
export const addressAPI = {
    search: (query) => api.get(`/addresses/search?q=${encodeURIComponent(query)}`),
    create: (data) => api.post('/addresses', data),
    get: (id) => api.get(`/addresses/${id}`),
    getReviews: (id) => api.get(`/addresses/${id}/reviews`),
    quickAdd: (data) => api.post('/addresses/quick-add', data).then(res => res.data),
    getLookupQuota: () => api.get('/addresses/lookup-quota')
};

// Property API (Phase 2)
export const propertyAPI = {
    getSummary: (id) => api.get(`/properties/${id}/summary`),
    get: (id) => api.get(`/properties/${id}`),
    getReviews: (id) => api.get(`/properties/${id}/reviews`),
    getArchivedReviews: (id) => api.get(`/properties/${id}/archived-reviews`),
    search: (query) => api.get(`/properties/search?q=${encodeURIComponent(query)}`),
    create: (data) => api.post('/properties', data),
};

// Verification API (Phase 2)
export const verificationAPI = {
    getStatus: () => api.get('/verification/status'),
    // submit handled via FormData/fetch
    
    // Admin endpoints
    getAll: (status) => api.get(`/admin/verifications${status ? `?status=${status}` : ''}`),
    getDetail: (id) => api.get(`/admin/verifications/${id}`),
    review: (id, data) => api.put(`/admin/verifications/${id}`, data),
    getDocument: (id) => api.get(`/admin/verifications/${id}/document`),
};

// Review API
export const reviewAPI = {
    create: (data) => api.post('/reviews', data),
    getMyReviews: () => api.get('/reviews/my'),
    getMyStats: () => api.get('/reviews/my/stats'),
    flag: (id, reason) => api.post(`/reviews/${id}/flag`, { reason }),
    uploadEvidence: (reviewId, formData) => api.post(`/reviews/${reviewId}/upload-evidence`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    // Quick Review Mode
    getExisting: (addressId) => api.get(`/reviews/existing/${addressId}`),
    quickUpdate: (reviewId, data) => api.put(`/reviews/${reviewId}/quick-update`, data),
    // Beta Safety Checks
    checkDuplicate: (addressId) => api.get(`/reviews/check-duplicate/${addressId}`),
    checkRateLimit: () => api.get('/reviews/check-rate-limit')
};

// Activity Feed API
export const activityAPI = {
    getRecent: () => api.get('/activity/recent'),
    getFeed: () => api.get('/activity/feed')
};

// Moderation API
export const moderationAPI = {
    getQueue: () => api.get('/moderation/queue'),
    action: (data) => api.post('/moderation/action', data),
    getLogs: () => api.get('/moderation/logs')
};

// Admin API
export const adminAPI = {
    getUsers: () => api.get('/admin/users'),
    getUsersPaginated: (page = 1, limit = 50, search = '') => 
        api.get(`/admin/users/paginated?page=${page}&limit=${limit}${search ? `&search=${encodeURIComponent(search)}` : ''}`),
    updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
    getAddresses: () => api.get('/admin/addresses'),
    resetOwnership: (data) => api.post('/admin/ownership-reset', data),
    getStats: () => api.get('/admin/stats'),
    // Reviews
    getAllReviews: () => api.get('/admin/reviews'),
    getPendingReviews: () => api.get('/admin/reviews/pending'),
    getPendingReviewsPaginated: (page = 1, limit = 20) => 
        api.get(`/admin/reviews/pending/paginated?page=${page}&limit=${limit}`),
    approveReview: (id) => api.post(`/admin/reviews/${id}/approve`),
    rejectReview: (id) => api.post(`/admin/reviews/${id}/reject`),
    hideReview: (id) => api.post(`/admin/reviews/${id}/hide`),
    deleteReview: (id) => api.delete(`/admin/reviews/${id}`),
    restoreReview: (id) => api.post(`/admin/reviews/${id}/restore`),
    getReviewDetails: (id) => api.get(`/admin/reviews/${id}/details`),
    requestEvidence: (id, message) => api.post(`/admin/reviews/${id}/request-evidence`, null, { params: { message } }),
    // User management
    deactivateUser: (id) => api.post(`/admin/users/${id}/deactivate`),
    reactivateUser: (id) => api.post(`/admin/users/${id}/reactivate`),
    suspendUser: (id, reason = '') => api.post(`/admin/users/${id}/suspend`, null, { params: { reason } }),
    getUserDetails: (id) => api.get(`/admin/users/${id}/details`),
    // Moderation strikes
    addStrike: (userId, reason = '') => api.post(`/admin/users/${userId}/add-strike`, null, { params: { reason } }),
    removeStrike: (userId) => api.post(`/admin/users/${userId}/remove-strike`),
    getStrikeHistory: (userId) => api.get(`/admin/users/${userId}/strike-history`),
    // Deletion requests
    getDeletionRequests: () => api.get('/admin/deletion-requests'),
    handleDeletionRequest: (id, action) => api.put(`/admin/deletion-requests/${id}?action=${action}`),
    // Audit logs
    getAuditLogs: () => api.get('/admin/audit-logs'),
    getAuditLogsPaginated: (page = 1, limit = 50, actionFilter = 'all') => 
        api.get(`/admin/audit-logs/paginated?page=${page}&limit=${limit}${actionFilter !== 'all' ? `&action_filter=${actionFilter}` : ''}`),
    // Invite codes
    getInviteCodes: () => api.get('/admin/invite-codes'),
    getInviteCode: (id) => api.get(`/admin/invite-codes/${id}`),
    createInviteCode: (data) => api.post('/admin/invite-codes', data),
    createBulkInviteCodes: (data) => api.post('/admin/invite-codes/bulk', data),
    updateInviteCode: (id, data) => api.put(`/admin/invite-codes/${id}`, data),
    disableInviteCode: (id) => api.delete(`/admin/invite-codes/${id}`),
    // Early adopters
    getEarlyAdopters: () => api.get('/admin/early-adopters'),
    updateEarlyAdopterStatus: (userId, data) => api.put(`/admin/users/${userId}/early-adopter`, data),
    // Referral leaderboard
    getReferralLeaderboard: () => api.get('/admin/referral-leaderboard'),
    // Feedback
    getFeedback: (page = 1, limit = 50, status = 'all') => 
        api.get(`/admin/feedback?page=${page}&limit=${limit}&status_filter=${status}`),
    updateFeedbackStatus: (id, status) => api.put(`/admin/feedback/${id}?new_status=${status}`),
    // Beta Analytics
    getAnalytics: () => api.get('/admin/analytics')
};

// Referral API
export const referralAPI = {
    getCode: () => api.get('/referral/code'),
    getStats: () => api.get('/referral/stats'),
    validate: (code) => api.get(`/referral/validate/${code}`),
    createInvite: (code) => api.post(`/referral/create-invite/${code}`)
};

// Feedback API
export const feedbackAPI = {
    submit: (data) => api.post('/feedback', data)
};

// Beta API
export const betaAPI = {
    getStatus: () => api.get('/beta/status')
};

export default api;
